import React from "react";
import Section from "../components/Section";
import { BRAND } from "../config/brandConfig";

function ApproachPage() {
  return (
    <div>
      {/* ===============================
         1. CORE PHILOSOPHY (GREEK + SANSKRIT)
         =============================== */}
      <Section
        eyebrow="Our approach"
        title="Calm, evidence-based, and deeply personal."
        kicker="We don’t claim to outsmart markets. We focus relentlessly on what we can actually control."
      >
        {/* Anchor line – makes it feel like a real philosophy, not fluff */}
        <p
          style={{
            fontSize: 15,
            color: BRAND.accent,
            fontWeight: 600,
            marginBottom: "1.5rem",
            maxWidth: 640,
          }}
        >
          Wealth planning is life planning. Your portfolio is only one piece of
          the story. Our job is to integrate all of it—so your wealth strategy
          mirrors the real world you live in.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr)",
            gap: "2.25rem",
            alignItems: "flex-start",
          }}
        >
          {/* Left column – the “what we actually control” list */}
          <div style={{ fontSize: 14, color: "#475569" }}>
            <p style={{ marginTop: 0 }}>
              We don’t waste your time with forecasts. Instead, we design around
              five levers we can truly govern:
            </p>
            <ul style={{ paddingLeft: "1.1rem", marginBottom: "1rem" }}>
              <li>
                <strong>Behavior</strong> – helping you stay grounded when
                headlines and emotions are loud.
              </li>
              <li>
                <strong>Costs</strong> – keeping friction low so more of the
                return belongs to you.
              </li>
              <li>
                <strong>Diversification</strong> – building portfolios meant to
                survive full cycles, not single moments.
              </li>
              <li>
                <strong>Tax efficiency</strong> – structuring accounts and
                withdrawals with the after-tax outcome in mind.
              </li>
              <li>
                <strong>Alignment</strong> – matching your investments with the
                actual decisions in front of your family or business.
              </li>
            </ul>
            <p>
              We draw on institutional research, behavioral finance, and decades
              of practitioner experience to build portfolios that endure across
              regimes—not just in backtests.
            </p>
          </div>

          {/* Right column – elevated “pillars” card */}
          <div
            style={{
              borderRadius: 18,
              padding: "1.4rem 1.5rem",
              backgroundColor: "#ffffff",
              border: "1px solid rgba(148,163,184,0.5)",
              boxShadow: "0 14px 40px rgba(15,23,42,0.08)",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: BRAND.accent,
                marginBottom: "0.35rem",
              }}
            >
              Core non-negotiables
            </div>
            <h3
              style={{
                marginTop: 0,
                marginBottom: "0.6rem",
                fontSize: 16,
                color: "#0f172a",
              }}
            >
              Three pillars we don’t compromise on
            </h3>
            <ul
              style={{
                margin: 0,
                paddingLeft: "1.1rem",
                fontSize: 13,
                color: "#475569",
              }}
            >
              <li>Fiduciary duty and full alignment with your interests.</li>
              <li>
                Clear communication of risk, cost, trade-offs, and alternatives.
              </li>
              <li>A repeatable, documented investment and review process.</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* ===============================
         2. SCENARIO PLANNING (UBS / RESILIENCE)
         =============================== */}
      <Section
        eyebrow="Scenario planning"
        title="We build strategies for the moments that matter."
        kicker="Complexity requires flexibility. We plan for the big forks in the road—long before you’re standing at them."
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)",
            gap: "2rem",
            alignItems: "stretch",
          }}
        >
          {/* Left – scenario tiles */}
          <div
            style={{
              padding: "1rem",
              backgroundColor: "#ffffff",
              borderRadius: 16,
              boxShadow: "0 10px 30px rgba(15,23,42,0.04)",
              border: "1px solid rgba(148,163,184,0.25)",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "1rem",
            }}
          >
            {[
              "Your company sells or you step back from leadership?",
              "You receive a concentrated equity grant or liquidity event?",
              "You move states—or countries—and your tax picture changes?",
              "A family member needs unexpected financial support?",
              "Markets dislocate sharply and headlines are screaming?",
              "Interest rates or inflation shift in ways that affect your plans?",
            ].map((scenario, index) => (
              <div
                key={index}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#0f172a",
                  borderLeft: `3px solid ${BRAND.accent}`,
                  paddingLeft: "0.75rem",
                  paddingTop: "0.35rem",
                  paddingBottom: "0.35rem",
                  background:
                    "linear-gradient(90deg, rgba(15,23,42,0.02), transparent)",
                }}
              >
                What happens if…{" "}
                <span style={{ fontWeight: 500, color: "#1f2933" }}>
                  {scenario}
                </span>
              </div>
            ))}
          </div>

          {/* Right – how we actually respond */}
          <div style={{ fontSize: 14, color: "#475569" }}>
            <p>
              We don’t scramble when these moments arrive—we’ve already mapped
              playbooks for them. Scenario planning with Harborline means:
            </p>
            <ul style={{ paddingLeft: "1.1rem" }}>
              <li>Knowing which levers to pull first, and which to ignore.</li>
              <li>
                Understanding tax and cash-flow implications before you sign
                anything.
              </li>
              <li>
                Having pre-agreed guardrails so decisions feel calm, not rushed.
              </li>
            </ul>
            <p>
              The result is a plan that can flex with your life instead of
              breaking at the first sign of change.
            </p>
          </div>
        </div>
      </Section>

      {/* ===============================
         3. BEHAVIORAL DISCIPLINE (VANGUARD / JAPAN)
         =============================== */}
      <Section
        eyebrow="The behavioral side of wealth"
        title="Behavioral discipline is the engine of long-term returns."
        kicker="Your plan only works if you can live with it. We help you stay invested, grounded, and aligned with what you said mattered most."
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)",
            gap: "2rem",
          }}
        >
          {/* Left – coaching & guardrails */}
          <div style={{ fontSize: 14, color: "#475569" }}>
            <p>
              Investment results don’t just come from markets—they come from how
              we respond to them. A large part of our work is helping you avoid
              the hidden tax of emotional decision-making.
            </p>
            <ul style={{ paddingLeft: "1.1rem" }}>
              <li>
                Staying invested through uncomfortable but normal volatility.
              </li>
              <li>
                Distinguishing between temporary price swings and real,
                permanent risk.
              </li>
              <li>
                Creating pre-agreed rules for when we act—and when we purposely
                do nothing.
              </li>
              <li>
                Keeping your plan anchored to long-term goals, not short-term
                narratives.
              </li>
            </ul>
          </div>

          {/* Right – “what we don’t / do” split card */}
          <div
            style={{
              borderRadius: 18,
              padding: "1.4rem 1.5rem",
              backgroundColor: "#ffffff",
              border: "1px solid rgba(148,163,184,0.45)",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                gap: "1.25rem",
              }}
            >
              <div>
                <h3
                  style={{
                    marginTop: 0,
                    fontSize: 14,
                    marginBottom: "0.4rem",
                    color: "#b91c1c",
                  }}
                >
                  What we don’t do
                </h3>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "1.1rem",
                    fontSize: 12,
                    color: "#6b7280",
                  }}
                >
                  <li>We don’t chase headlines or fads.</li>
                  <li>We don’t pretend to know the next quarter.</li>
                  <li>We don’t market-time your future.</li>
                  <li>We don’t sell products to hit quotas.</li>
                  <li>We don’t complicate simple truths.</li>
                </ul>
              </div>
              <div>
                <h3
                  style={{
                    marginTop: 0,
                    fontSize: 14,
                    marginBottom: "0.4rem",
                    color: BRAND.accent,
                  }}
                >
                  What we do instead
                </h3>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "1.1rem",
                    fontSize: 12,
                    color: "#6b7280",
                  }}
                >
                  <li>We stay disciplined when others react.</li>
                  <li>We manage risk, taxes, and cash flow in concert.</li>
                  <li>We put guardrails around big, emotional decisions.</li>
                  <li>We build plans that are simple to follow.</li>
                  <li>We help you stay the course when it matters most.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

export default ApproachPage;
